import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star, Scroll, Home, Gem, Heart, Briefcase, ArrowRight } from 'lucide-react';

const WHATSAPP_BASE = 'https://wa.me/971541873527?text=';

const services = [
  {
    icon: Star,
    title: 'Horoscope Reading',
    subtitle: 'Celestial Insights',
    description: 'Receive comprehensive insights into your daily, weekly, monthly, and yearly horoscope predictions based on your unique birth chart.',
    color: 'from-yellow-500/20 to-amber-600/10',
    whatsappMsg: 'Hello, I would like to schedule a Horoscope Reading consultation',
  },
  {
    icon: Scroll,
    title: 'Kundli Matching',
    subtitle: 'Compatibility Analysis',
    description: 'Ensure a harmonious and prosperous marriage with comprehensive Kundli matching analysis for lasting compatibility.',
    color: 'from-purple-500/20 to-violet-600/10',
    whatsappMsg: 'Hello, I am interested in Kundli Matching services',
  },
  {
    icon: Home,
    title: 'Vastu Consultation',
    subtitle: 'Spatial Harmony',
    description: 'Transform your living and working spaces with expert Vastu Shastra guidance for positive energy flow and success.',
    color: 'from-blue-500/20 to-cyan-600/10',
    whatsappMsg: 'Hello, I would like a Vastu Consultation for my property',
  },
  {
    icon: Gem,
    title: 'Gemstone Guidance',
    subtitle: 'Crystal Healing',
    description: 'Discover the perfect gemstones to enhance your fortune, vitality, and prosperity based on your planetary alignments.',
    color: 'from-emerald-500/20 to-green-600/10',
    whatsappMsg: 'Hello, I am seeking Gemstone Guidance recommendations',
  },
  {
    icon: Heart,
    title: 'Love & Marriage',
    subtitle: 'Relationship Harmony',
    description: 'Find celestial solutions for relationship challenges, love compatibility, marriage timing, and family harmony.',
    color: 'from-rose-500/20 to-pink-600/10',
    whatsappMsg: 'Hello, I need guidance regarding Love and Marriage matters',
  },
  {
    icon: Briefcase,
    title: 'Career Guidance',
    subtitle: 'Professional Success',
    description: 'Navigate your professional journey with astrological insights for career advancement, transitions, and business prosperity.',
    color: 'from-orange-500/20 to-amber-600/10',
    whatsappMsg: 'Hello, I would like Career Guidance consultation',
  },
];

const ServiceCard = ({ service, index, isInView }) => {
  const Icon = service.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="service-card group relative backdrop-blur-md bg-white/5 border border-white/10 p-8 rounded-2xl overflow-hidden"
      data-testid={`service-card-${index}`}
    >
      {/* Background Gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
      
      {/* Content */}
      <div className="relative z-10">
        {/* Icon */}
        <div className="service-icon">
          <Icon className="text-[#D4AF37]" size={32} />
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-[#F8FAFC] mb-2">{service.title}</h3>
        <p className="text-[#D4AF37] text-sm mb-4 font-medium">{service.subtitle}</p>

        {/* Description */}
        <p className="text-[#94A3B8] text-sm leading-relaxed mb-6">
          {service.description}
        </p>

        {/* CTA */}
        <a
          href={`${WHATSAPP_BASE}${encodeURIComponent(service.whatsappMsg)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-[#D4AF37] font-medium text-sm group-hover:text-[#FCD34D] transition-colors"
          data-testid={`service-cta-${index}`}
        >
          <span>Schedule Consultation</span>
          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </a>
      </div>
    </motion.div>
  );
};

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="services"
      className="relative py-20 md:py-32"
      data-testid="services-section"
      ref={ref}
    >
      {/* Section Divider */}
      <div className="section-divider mb-20" />

      {/* Background Glow */}
      <div className="cosmic-glow absolute top-1/3 right-0 opacity-20" />
      <div className="cosmic-glow absolute bottom-1/3 left-0 opacity-15" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="section-header"
        >
          <span className="text-[#D4AF37] text-sm uppercase tracking-widest font-medium">
            Our Services
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-4">
            Divine <span className="text-gold-gradient">Spiritual Services</span>
          </h2>
          <p className="mt-4">
            Expert guidance for all aspects of life through ancient Vedic wisdom
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, index) => (
            <ServiceCard
              key={service.title}
              service={service}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-12"
        >
          <p className="text-[#94A3B8] mb-6">
            Not sure which service suits your needs? <span className="text-[#D4AF37]">Let us guide you.</span>
          </p>
          <a
            href={`${WHATSAPP_BASE}${encodeURIComponent('Hello, I would like guidance on choosing the right consultation service')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 cta-gold"
            data-testid="services-main-cta"
          >
            <span>Get Free Consultation</span>
            <ArrowRight size={20} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
